// Chemin: C:\gestion-copro\src\services\demo.service.ts
// Description: Service frontend — gestion des démos commerciales
//              CRUD collection Firestore `demos`, appels Firebase Functions
//              Lecture/écriture config `config/demo` (liens Calendly + inscription)
// Session 239 : Nouveau
// Session 241 : Nouveaux statuts (programmee, envoyee, active, terminee, resetee)
//               Ajout envoyerAccesDemo, enregistrerPremierLogin
//               dateDebut optionnel dans CreerDemoParams
//               dateExpiration optionnelle dans Demo (absente jusqu'au premier login)

import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

// ============================================================================
// TYPES
// ============================================================================

export type StatutDemo = 'programmee' | 'envoyee' | 'active' | 'terminee' | 'resetee';

export interface Demo {
  id: string;
  prospectNom: string;
  prospectEmail: string;
  scenarioId: string;
  scenarioNom: string;
  uid: string;
  emailDemo: string;
  coproprieteId: string;
  dureeMinutes: number;
  quotaAnalyses: number;
  analysesConsommees: number;
  dateCreation: Date;
  dateExpiration?: Date; // absente jusqu'au premier login
  dateProgrammee?: Date; // présente si statut 'programmee'
  datePremierLogin?: Date;
  dateEnvoiAcces?: Date;
  statut: StatutDemo;
  briefSentinelle: string;
  dateTerminaison?: Date;
}

export interface Scenario {
  id: string;
  nom: string;
  cible: 'cs_actif' | 'cs_debutant' | 'vigie_solo';
  briefSentinelle: string;
  nomCopropriete?: string;
  nombreLots?: number;
}

export interface ConfigDemo {
  lienCalendly: string;
  lienInscription: string;
}

export interface CreerDemoParams {
  prospectNom: string;
  prospectEmail: string;
  scenarioId: string;
  dureeMinutes: number;
  quotaAnalyses: number;
  dateDebut?: string; // ISO string optionnel — absent = démo immédiate
}

export interface CreerDemoResult {
  success: boolean;
  demoId: string;
  uid: string;
  email: string;
  motDePasse: string;
  statut: 'envoyee' | 'programmee';
}

// ============================================================================
// SCÉNARIOS
// ============================================================================

export async function chargerScenarios(): Promise<Scenario[]> {
  const snap = await getDocs(collection(db, 'scenarios'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Scenario));
}

// ============================================================================
// DÉMOS
// ============================================================================

export async function chargerDemos(): Promise<Demo[]> {
  const q = query(collection(db, 'demos'), orderBy('dateCreation', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      prospectNom: data.prospectNom || '',
      prospectEmail: data.prospectEmail || '',
      scenarioId: data.scenarioId || '',
      scenarioNom: data.scenarioNom || data.scenarioId || '',
      uid: data.uid || '',
      emailDemo: data.emailDemo || '',
      coproprieteId: data.coproprieteId || '',
      dureeMinutes: data.dureeMinutes || 60,
      quotaAnalyses: data.quotaAnalyses || 10,
      analysesConsommees: data.analysesConsommees || 0,
      dateCreation: (data.dateCreation as Timestamp)?.toDate() || new Date(),
      dateExpiration: data.dateExpiration
        ? (data.dateExpiration as Timestamp).toDate()
        : undefined,
      dateProgrammee: data.dateProgrammee
        ? (data.dateProgrammee as Timestamp).toDate()
        : undefined,
      datePremierLogin: data.datePremierLogin
        ? (data.datePremierLogin as Timestamp).toDate()
        : undefined,
      dateEnvoiAcces: data.dateEnvoiAcces
        ? (data.dateEnvoiAcces as Timestamp).toDate()
        : undefined,
      statut: (data.statut as StatutDemo) || 'envoyee',
      briefSentinelle: data.briefSentinelle || '',
      dateTerminaison: data.dateTerminaison
        ? (data.dateTerminaison as Timestamp).toDate()
        : undefined,
    } as Demo;
  });
}

/**
 * Crée un nouveau compte démo via Firebase Function.
 * Si dateDebut est absent ou passé → démo immédiate (email envoyé, statut 'envoyee').
 * Si dateDebut est dans le futur → démo programmée (statut 'programmee', email retenu).
 */
export async function creerDemo(params: CreerDemoParams): Promise<CreerDemoResult> {
  const fn = httpsCallable<CreerDemoParams, CreerDemoResult>(functions, 'creerCompteDemo');
  const result = await fn(params);
  return result.data;
}

/**
 * Envoie les accès au prospect pour une démo programmée.
 * Passe le statut 'programmee' → 'envoyee'.
 * Peut aussi être utilisé en re-envoi depuis 'envoyee'.
 */
export async function envoyerAccesDemo(demoId: string): Promise<void> {
  const fn = httpsCallable<{ demoId: string }, { success: boolean }>(
    functions,
    'envoyerAccesDemo'
  );
  await fn({ demoId });
}

/**
 * Enregistre le premier login d'un compte démo.
 * Appelé depuis AuthContext au login si l'utilisateur est un compte démo.
 * Idempotent — sans effet si déjà enregistré.
 */
export async function enregistrerPremierLoginDemo(): Promise<{
  premierLogin: boolean;
  dateExpiration?: string;
}> {
  const fn = httpsCallable<Record<string, never>, {
    success: boolean;
    premierLogin: boolean;
    dateExpiration?: string;
  }>(functions, 'enregistrerPremierLogin');
  const result = await fn({});
  return {
    premierLogin: result.data.premierLogin,
    dateExpiration: result.data.dateExpiration,
  };
}

/**
 * Termine une démo (met à jour statut + envoie email fin de démo)
 */
export async function terminerDemo(demoId: string): Promise<void> {
  const fn = httpsCallable<{ demoId: string }, { success: boolean }>(functions, 'terminerDemo');
  await fn({ demoId });
}

/**
 * Prolonge une démo active en repoussant la date d'expiration
 */
export async function prolongerDemo(demoId: string, minutesSupplementaires: number): Promise<void> {
  const demoRef = doc(db, 'demos', demoId);
  const demoSnap = await getDoc(demoRef);
  if (!demoSnap.exists()) throw new Error(`Démo ${demoId} introuvable`);

  const data = demoSnap.data();

  // dateExpiration peut être absente si le prospect ne s'est pas encore connecté
  if (!data.dateExpiration) {
    // Prolonger = augmenter dureeMinutes — la dateExpiration sera calculée au premier login
    const nouvelleDuree = (data.dureeMinutes || 60) + minutesSupplementaires;
    await updateDoc(demoRef, { dureeMinutes: nouvelleDuree });
    return;
  }

  const dateExpiration = (data.dateExpiration as Timestamp).toDate();
  const nouvelleDateExpiration = new Date(
    dateExpiration.getTime() + minutesSupplementaires * 60 * 1000
  );
  const dateCreation = (data.dateCreation as Timestamp).toDate();
  const nouvelleDureeMinutes = Math.round(
    (nouvelleDateExpiration.getTime() - dateCreation.getTime()) / (1000 * 60)
  );

  await updateDoc(demoRef, {
    dateExpiration: Timestamp.fromDate(nouvelleDateExpiration),
    dureeMinutes: nouvelleDureeMinutes,
  });
}

// ============================================================================
// CONFIGURATION DÉMO
// ============================================================================

export async function chargerConfigDemo(): Promise<ConfigDemo> {
  const snap = await getDoc(doc(db, 'config', 'demo'));
  if (!snap.exists()) return { lienCalendly: '', lienInscription: '' };
  const data = snap.data();
  return {
    lienCalendly: data.lienCalendly || '',
    lienInscription: data.lienInscription || '',
  };
}

export async function sauvegarderConfigDemo(config: ConfigDemo): Promise<void> {
  const { setDoc } = await import('firebase/firestore');
  await setDoc(doc(db, 'config', 'demo'), config, { merge: true });
}

// ============================================================================
// SUPPRESSION
// ============================================================================

export async function supprimerDemo(demoId: string): Promise<void> {
  const fn = httpsCallable<{ demoId: string }, { success: boolean }>(
    functions,
    'supprimerCompteDemo'
  );
  await fn({ demoId });
}

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Calcule le temps restant d'une démo active.
 * Retourne null si la démo n'est pas active, expirée, ou si dateExpiration absente.
 */
export function calculerTempsRestant(demo: Demo): { heures: number; minutes: number } | null {
  if (demo.statut !== 'active') return null;
  if (!demo.dateExpiration) return null;
  const maintenant = new Date();
  const diff = demo.dateExpiration.getTime() - maintenant.getTime();
  if (diff <= 0) return null;
  const heures = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { heures, minutes };
}

export function libellStatutDemo(statut: StatutDemo): string {
  switch (statut) {
    case 'programmee': return 'Programmée';
    case 'envoyee':    return 'Accès envoyés';
    case 'active':     return 'Active';
    case 'terminee':   return 'Terminée';
    case 'resetee':    return 'Réinitialisée';
    default:           return statut;
  }
}

export function couleurStatutDemo(statut: StatutDemo): string {
  switch (statut) {
    case 'programmee': return 'orange';
    case 'envoyee':    return 'blue';
    case 'active':     return 'green';
    case 'terminee':   return 'gray';
    case 'resetee':    return 'purple';
    default:           return 'gray';
  }
}
